import React from "react";

const LeaveTypeCard = ({
  leaveType,
  onClick,
  isDarkMode,
  screenSize,
  fonts,
  spacing,
  theme,
  t,
}) => {
  // Super aggressive safety check - return immediately if any property is missing
  if (
    !leaveType ||
    typeof leaveType !== "object" ||
    !leaveType?.icon ||
    !leaveType?.id ||
    !leaveType?.name ||
    !leaveType?.color
  ) {
    if (process.env.NODE_ENV !== "production") {
      console.warn("[LeaveTypeCard] Missing required properties:", {
        leaveType,
        hasIcon: leaveType?.icon !== undefined,
        hasId: leaveType?.id !== undefined,
        hasName: leaveType?.name !== undefined,
        hasColor: leaveType?.color !== undefined,
      });
    }
    return null;
  }

  const TypeIcon = leaveType.icon;
  const usedPercentage = leaveType.available
    ? Math.min(100, Math.max(0, (leaveType.used / leaveType.available) * 100))
    : 0;
  const remaining = Math.max(
    (leaveType.available || 0) - (leaveType.used || 0),
    0
  );

  const cardStyle = {
    background: isDarkMode
      ? "rgba(255, 255, 255, 0.08)"
      : "rgba(255, 255, 255, 0.9)",
    backdropFilter: "blur(25px)",
    WebkitBackdropFilter: "blur(25px)",
    border: isDarkMode
      ? "2px solid rgba(255, 255, 255, 0.1)"
      : "2px solid rgba(0, 0, 0, 0.05)",
    borderRadius: screenSize === "xs" ? "16px" : "24px",
    padding: screenSize === "xs" ? "20px" : "28px",
    transition: "all 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
    cursor: "pointer",
    position: "relative",
    overflow: "hidden",
  };

  return (
    <div onClick={onClick} style={cardStyle}>
      {/* Background decoration */}
      <div
        style={{
          position: "absolute",
          top: "-30px",
          left: "-30px",
          width: screenSize === "xs" ? "60px" : "100px",
          height: screenSize === "xs" ? "60px" : "100px",
          borderRadius: "50%",
          filter: "blur(25px)",
        }}
      />

      {/* Header */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: screenSize === "xs" ? "12px" : "16px",
          marginBottom: screenSize === "xs" ? "16px" : "24px",
          position: "relative",
          zIndex: 2,
          flexDirection: screenSize === "xs" ? "column" : "row",
          textAlign: screenSize === "xs" ? "center" : "left",
        }}
      >
        <div
          style={{
            width: screenSize === "xs" ? "48px" : "64px",
            height: screenSize === "xs" ? "48px" : "64px",
            background:
              leaveType.gradient ||
              `linear-gradient(135deg, ${leaveType.color}, ${leaveType.color}dd)`,
            borderRadius: screenSize === "xs" ? "16px" : "20px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            boxShadow: `0 8px 24px ${leaveType.color}30`,
            flexShrink: 0,
          }}
        >
          {React.createElement(TypeIcon, {
            size: screenSize === "xs" ? 20 : 28,
            color: "#ffffff",
          })}
        </div>

        <div style={{ flex: 1, minWidth: 0 }}>
          <h3
            style={{
              color: isDarkMode ? "white" : "#1a1a1a",
              fontSize: screenSize === "xs" ? fonts.body : fonts.subtitle,
              fontWeight: "600",
              margin: "0 0 6px 0",
              textShadow: isDarkMode ? "0 2px 4px rgba(0,0,0,0.3)" : "none",
              fontFamily: "'Cairo', sans-serif",
            }}
          >
            {leaveType.name}
          </h3>
          <p
            style={{
              color: isDarkMode
                ? "rgba(255, 255, 255, 0.6)"
                : "rgba(26, 26, 26, 0.6)",
              fontSize: screenSize === "xs" ? fonts.tiny : fonts.small,
              margin: 0,
              lineHeight: 1.4,
              fontFamily: "'Cairo', sans-serif",
            }}
          >
            {leaveType.description}
          </p>
        </div>
      </div>

      {/* Stats Grid */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(3, 1fr)",
          gap: screenSize === "xs" ? "12px" : "20px",
          marginBottom: screenSize === "xs" ? "16px" : "20px",
          position: "relative",
          zIndex: 2,
        }}
      >
        {[
          { label: t.balance.used, value: leaveType.used, color: leaveType.color },
          { label: t.balance.total, value: leaveType.available, color: isDarkMode ? "white" : "#1a1a1a" },
          { label: t.balance.left, value: remaining, color: theme.colors.success },
        ].map(({ label, value, color }) => (
          <div key={label} style={{ textAlign: "center" }}>
            <div
              style={{
                color: isDarkMode
                  ? "rgba(255, 255, 255, 0.6)"
                  : "rgba(26, 26, 26, 0.6)",
                fontSize: fonts.tiny,
                fontWeight: "500",
                marginBottom: "6px",
                textTransform: "uppercase",
                letterSpacing: "0.5px",
                fontFamily: "'Cairo', sans-serif",
              }}
            >
              {label}
            </div>
            <div
              style={{
                color,
                fontSize: screenSize === "xs" ? fonts.subtitle : fonts.title,
                fontWeight: "700",
                textShadow: isDarkMode ? "0 2px 4px rgba(0,0,0,0.3)" : "none",
                fontFamily: "'Cairo', sans-serif",
              }}
            >
              {value}
            </div>
          </div>
        ))}
      </div>

      {/* Progress Bar */}
      <div style={{ position: "relative", zIndex: 2 }}>
        <div
          style={{
            width: "100%",
            height: screenSize === "xs" ? "6px" : "8px",
            background: `${leaveType.color}20`,
            borderRadius: "4px",
            overflow: "hidden",
            position: "relative",
          }}
        >
          <div
            style={{
              width: `${usedPercentage}%`,
              height: "100%",
              background: leaveType.gradient || leaveType.color,
              borderRadius: "4px",
              transition: "width 0.6s ease",
              position: "relative",
            }}
          >
            <div
              style={{
                position: "absolute",
                top: 0,
                right: "-100%",
                width: "100%",
                height: "100%",
                background:
                  "linear-gradient(90deg, transparent, rgba(255,255,255,0.4), transparent)",
                animation: usedPercentage > 0 ? "shine 2s infinite" : "none",
              }}
            />
          </div>
        </div>

        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            marginTop: "12px",
            fontSize: fonts.tiny,
            color: isDarkMode
              ? "rgba(255, 255, 255, 0.6)"
              : "rgba(26, 26, 26, 0.6)",
            fontFamily: "'Cairo', sans-serif",
          }}
        >
          <span style={{ fontWeight: "600" }}>
            {Math.round(usedPercentage)}% {t.balance.percentUsed}
          </span>
        </div>
      </div>
    </div>
  );
};

export default LeaveTypeCard;